// src/hooks/useReAuth.js
import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import auditService from '../services/auditService';

const useReAuth = () => {
  const [showReAuthModal, setShowReAuthModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { currentUser, userProfile } = useAuth();

  // Check if re-authentication is required
  const requiresReAuth = useCallback((actionType = 'sensitive_action') => {
    if (!currentUser) return false;

    const lastReAuth = sessionStorage.getItem('lastReAuth');
    const loginTime = sessionStorage.getItem('loginTime');
    
    // If no re-auth has been done, check session duration
    if (!lastReAuth && loginTime) {
      const sessionDuration = Date.now() - parseInt(loginTime);
      const maxSessionWithoutReAuth = 12 * 60 * 60 * 1000; // 12 hours
      
      return sessionDuration > maxSessionWithoutReAuth;
    }
    
    // If re-auth was done, check if it's still valid (24 hours)
    if (lastReAuth) {
      const timeSinceReAuth = Date.now() - parseInt(lastReAuth);
      const reAuthValidityPeriod = 24 * 60 * 60 * 1000; // 24 hours
      
      return timeSinceReAuth > reAuthValidityPeriod;
    }
    
    return true; // Default to requiring re-auth if uncertain
  }, [currentUser]);

  // Execute action with re-auth check
  const executeWithReAuth = useCallback(async (action, actionType = 'sensitive_action') => {
    if (!currentUser || !userProfile) {
      throw new Error('User not authenticated');
    }

    // Check if re-authentication is required
    if (requiresReAuth(actionType)) {
      return new Promise((resolve, reject) => {
        setPendingAction({
          action,
          actionType,
          resolve,
          reject
        });
        setShowReAuthModal(true);
      });
    }

    // Execute action directly if no re-auth needed
    try {
      const result = await action();
      
      // Log the sensitive action
      await auditService.logSensitiveAction(currentUser.uid, userProfile, actionType, {
        timestamp: new Date().toISOString(),
        reAuthRequired: false
      });
      
      return result;
    } catch (error) {
      console.error('Action execution failed:', error);
      throw error;
    }
  }, [currentUser, userProfile, requiresReAuth]);

  // Handle successful re-authentication
  const handleReAuthSuccess = useCallback(async () => {
    if (!pendingAction) return;

    try {
      const result = await pendingAction.action();
      
      // Log the sensitive action with re-auth
      await auditService.logSensitiveAction(currentUser.uid, userProfile, pendingAction.actionType, {
        timestamp: new Date().toISOString(),
        reAuthRequired: true,
        reAuthCompleted: true
      });
      
      pendingAction.resolve(result);
    } catch (error) {
      pendingAction.reject(error);
    } finally {
      setPendingAction(null);
      setShowReAuthModal(false);
    }
  }, [pendingAction, currentUser, userProfile]);

  // Handle re-auth cancellation
  const handleReAuthCancel = useCallback(() => {
    if (pendingAction) {
      pendingAction.reject(new Error('Re-authentication cancelled by user'));
      setPendingAction(null);
    }
    setShowReAuthModal(false);
  }, [pendingAction]);

  // Get time until re-auth is required
  const getTimeUntilReAuth = useCallback(() => {
    const lastReAuth = sessionStorage.getItem('lastReAuth');
    const loginTime = sessionStorage.getItem('loginTime');
    
    if (lastReAuth) {
      const timeSinceReAuth = Date.now() - parseInt(lastReAuth);
      const reAuthValidityPeriod = 24 * 60 * 60 * 1000; // 24 hours
      return Math.max(0, reAuthValidityPeriod - timeSinceReAuth);
    }
    
    if (loginTime) {
      const sessionDuration = Date.now() - parseInt(loginTime);
      const maxSessionWithoutReAuth = 12 * 60 * 60 * 1000; // 12 hours
      return Math.max(0, maxSessionWithoutReAuth - sessionDuration);
    }
    
    return 0;
  }, []);

  return {
    showReAuthModal,
    executeWithReAuth,
    handleReAuthSuccess,
    handleReAuthCancel,
    requiresReAuth,
    getTimeUntilReAuth,
    pendingActionType: pendingAction?.actionType
  };
};

export default useReAuth;
